import { useState, useEffect, useRef } from 'react';
import styles from '../assets/styles/UserAccess.module.scss';
import { useLocation } from 'react-router-dom';
import { ROLE_MAP, BUDGET_MAP } from '../utils/constants';

interface CorporateSearchResult {
  name: string;
  corporate_number: string;
  prefecture?: string;
  city?: string;
  street?: string;
  address?: string;
}

const PreRegister = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const status = searchParams.get('status');
  const [isLoading, setIsLoading] = useState(false);

  // 新規登録
  const [regTenantName, setRegTenantName] = useState<string>('');
  const [regLastName, setRegLastName] = useState<string>('');
  const [regFirstName, setRegFirstName] = useState<string>('');
  const [regEmail, setRegEmail] = useState<string>('');
  const [regPwd, setRegPwd] = useState<string>('');
  const [regPwdConf, setRegPwdConf] = useState<string>('');

  // 新規追加項目
  const [regTelNum, setRegTelNum] = useState<string>('');
  const [regPosition, setRegPosition] = useState<string>('');
  const [regBudget, setRegBudget] = useState<string>('');
  const [regAgree, setRegAgree] = useState<boolean>(false);
  const [regCorporateNumber, setRegCorporateNumber] = useState<string>('');
  const [noCorporateNumber, setNoCorporateNumber] = useState<boolean>(false);
  const [regPrefecture, setRegPrefecture] = useState<string>('');
  const [regCity, setRegCity] = useState<string>('');
  const [regStreet, setRegStreet] = useState<string>('');

  // 企業名検索用
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchTab, setSearchTab] = useState<'name' | 'number'>('name');
  const [searchNameQuery, setSearchNameQuery] = useState('');
  const [searchNumberQuery, setSearchNumberQuery] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<CorporateSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // モーダルを開くハンドラー
  const handleOpenModal = () => {
    setShowSearchModal(true);
    setSearchNameQuery('');
    setSearchNumberQuery(null);
    setCandidates([]);
  };

  const handleSearchByName = async () => {
    if (searchNameQuery.trim().length < 2) {
      alert('2文字以上入力してください');
      return;
    }
    setIsSearching(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/nta/companies?name=${encodeURIComponent(searchNameQuery)}`
      );
      const data = await res.json();
      if (data.status === 'success' && data.data) {
        setCandidates(data.data);
      } else {
        setCandidates([]);
        alert('該当する企業が見つかりませんでした。');
      }
    } catch (error) {
      console.error('法人検索エラー', error);
      alert('検索中にエラーが発生しました。');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchByNumber = async () => {
    if (!searchNumberQuery || searchNumberQuery.trim().length !== 13) {
      alert('13桁の法人番号を入力してください');
      return;
    }
    setIsSearching(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/nta/companies/number?number=${encodeURIComponent(searchNumberQuery)}`
      );
      const data = await res.json();
      if (data.status === 'success' && data.data) {
        setCandidates(data.data);
      } else {
        setCandidates([]);
        alert('該当する企業が見つかりませんでした。');
      }
    } catch (error) {
      console.error('法人番号検索エラー', error);
      alert('検索中にエラーが発生しました。');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectCompany = (comp: CorporateSearchResult) => {
    setRegTenantName(comp.name);
    setRegCorporateNumber(comp.corporate_number);
    setRegPrefecture(comp.prefecture || '');
    setRegCity(comp.city || '');
    setRegStreet(comp.street || '');
    setShowSearchModal(false);
  };

  const isFormValid =
    regTenantName?.trim() &&
    regLastName?.trim() &&
    regFirstName?.trim() &&
    regEmail?.trim() &&
    regTelNum?.trim() &&
    regPosition &&
    regBudget &&
    regPwd?.trim() &&
    regPwdConf?.trim() &&
    regPwd === regPwdConf &&
    regAgree &&
    (regCorporateNumber || noCorporateNumber);

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    try {
      setIsLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/pre-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenant_name: regTenantName,
          corporate_number: regCorporateNumber || null,
          has_no_corporate_number: noCorporateNumber,
          last_name: regLastName,
          first_name: regFirstName,
          email: regEmail,
          tel_num: regTelNum,
          position: regPosition,
          budget: regBudget,
          password: regPwd,
          agree: regAgree,
          prefecture: regPrefecture || null,
          city: regCity || null,
          address1: regStreet || null,
        }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        window.location.href = '/pre-register/thanks';
      } else {
        alert(`登録に失敗しました：${data.message}`);
      }
    } catch (error) {
      console.error('送信エラー:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    window.location.href = '/login';
  };

  const handleTermRedirect = () => {
    window.open('/terms', '_blank');
  };

  const handlePrivacyRedirect = () => {
    window.open('/privacy-policy', '_blank');
  };

  return (
    <div className={styles.containerWrap}>
      <div className={styles.backgroundGlow}>
        <div className={styles.glowBlue} />
        <div className={styles.glowPink} />
      </div>
      <a href="/" className={styles.backLink}>
        ← トップページへ戻る
      </a>

      <div className={styles.container}>
        <div className={styles.userAccessContent}>
          <div className={styles.userAccessContentInner}>
            <div className={styles.userAccessFrom}>
              {(status === 'loggedout' || status === 'need_login') && (
                <div className={styles.paramTxt}>
                  {status === 'loggedout' ? 'ログアウトしました！' : 'ログインしてください'}
                </div>
              )}

              <img src="/images/logo_aibex_line_b.svg" alt="AI-BEX" className={styles.logo} />

              <h1>新規登録</h1>

              <form onSubmit={handleRegisterSubmit}>
                <label>企業名</label>
                <div className={styles.companyInputWrap}>
                  <input
                    type="text"
                    value={regTenantName}
                    onChange={() => {}}
                    onClick={handleOpenModal}
                    placeholder="クリックして法人を設定してください"
                    required
                    readOnly={true}
                    className={`${styles.inputTxt} ${styles.clickableInput}`}
                  />
                  {regCorporateNumber && !noCorporateNumber && (
                    <div className={styles.corpNumberDisplay}>法人番号: {regCorporateNumber}</div>
                  )}
                </div>
                <div className={styles.userName}>
                  <div className={styles.formGroupItem}>
                    <label>ユーザー名(姓)</label>
                    <input
                      type="text"
                      value={regLastName}
                      onChange={(e) => setRegLastName(e.target.value)}
                      placeholder="姓"
                      required
                      className={styles.inputTxt}
                    />
                  </div>
                  <div className={styles.formGroupItem}>
                    <label>ユーザー名(名)</label>
                    <input
                      type="text"
                      value={regFirstName}
                      onChange={(e) => setRegFirstName(e.target.value)}
                      placeholder="名"
                      required
                      className={styles.inputTxt}
                    />
                  </div>
                </div>
                <label>ID(メールアドレス)</label>
                <input
                  type="email"
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  placeholder="email@example.com"
                  required
                  className={styles.inputTxt}
                />

                <label>つながりやすい電話番号</label>
                <input
                  type="tel"
                  value={regTelNum}
                  onChange={(e) => setRegTelNum(e.target.value)}
                  placeholder="03-1234-5678"
                  required
                  className={styles.inputTxt}
                />

                <label>貴社の立場を教えてください</label>
                <select
                  value={regPosition}
                  onChange={(e) => setRegPosition(e.target.value)}
                  required
                >
                  <option value="" disabled>
                    選択してください
                  </option>
                  {Object.entries(ROLE_MAP).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </select>

                <label>月間の広告ご予算</label>
                <select value={regBudget} onChange={(e) => setRegBudget(e.target.value)} required>
                  <option value="" disabled>
                    選択してください
                  </option>
                  {Object.entries(BUDGET_MAP).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </select>

                <div className={styles.userName}>
                  <div className={styles.formGroupItem}>
                    <label>パスワード</label>
                    <input
                      type="password"
                      value={regPwd}
                      onChange={(e) => setRegPwd(e.target.value)}
                      placeholder="パスワードを入力"
                      required
                      className={styles.inputTxt}
                    />
                  </div>
                  <div className={styles.formGroupItem}>
                    <label>パスワード確認</label>
                    <input
                      type="password"
                      value={regPwdConf}
                      onChange={(e) => setRegPwdConf(e.target.value)}
                      placeholder="パスワード確認を入力"
                      required
                      className={styles.inputTxt}
                    />
                  </div>
                </div>
                <p className={styles.passwordHint}>
                  *8文字以上、英語(大文字、小文字を含む) + 数字を含めてください。
                </p>

                <div className={styles.checkboxGroup}>
                  <input
                    type="checkbox"
                    id="regAgree"
                    checked={regAgree}
                    onChange={(e) => setRegAgree(e.target.checked)}
                    required
                  />
                  <label htmlFor="regAgree">
                    <span onClick={handleTermRedirect}>利用規約</span>
                    および、
                    <span onClick={handlePrivacyRedirect}>プライバシーポリシー</span>
                    に同意します。
                  </label>
                </div>

                <button type="submit" disabled={!isFormValid || isLoading}>
                  {isLoading ? 'Loading...' : '新規登録'}
                </button>
                <div className={styles.registerLink}>
                  <a onClick={handleLoginRedirect}>既にアカウントをお持ちの方はこちら</a>
                </div>
              </form>
            </div>
          </div>

          <div className={styles.apiDisclaimer}>
            ※法人番号検索は、国税庁法人番号システムのWeb-API機能を利用しています。
            <br />
            サービス内容は国税庁によって保証されたものではありません。
          </div>
        </div>
      </div>

      {showSearchModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>法人を検索</h3>
              <button onClick={() => setShowSearchModal(false)} className={styles.modalClose}>
                &times;
              </button>
            </div>

            <div className={styles.modalTabs}>
              <div
                onClick={() => {
                  setSearchTab('name');
                  setCandidates([]);
                }}
                className={`${styles.modalTab} ${searchTab === 'name' ? styles.active : ''}`}
              >
                企業名で検索
              </div>
              <div
                onClick={() => {
                  setSearchTab('number');
                  setCandidates([]);
                }}
                className={`${styles.modalTab} ${searchTab === 'number' ? styles.active : ''}`}
              >
                法人番号で検索
              </div>
            </div>

            <div className={styles.modalSearchWrap}>
              {searchTab === 'name' ? (
                <>
                  <input
                    type="text"
                    value={searchNameQuery}
                    onChange={(e) => setSearchNameQuery(e.target.value)}
                    placeholder="企業名を入力 (例: ソフト)"
                  />
                  <button onClick={handleSearchByName} disabled={isSearching}>
                    検索
                  </button>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    value={searchNumberQuery || ''}
                    onChange={(e) =>
                      setSearchNumberQuery(
                        e.target.value.replace(/[^0-9]/g, '').slice(0, 13) || null
                      )
                    }
                    placeholder="13桁の法人番号を入力"
                  />
                  <button onClick={handleSearchByNumber} disabled={isSearching}>
                    検索
                  </button>
                </>
              )}
            </div>

            <div className={styles.modalResultContainer}>
              {isSearching ? (
                <div className={styles.modalEmptyState}>検索中...</div>
              ) : candidates.length > 0 ? (
                <div>
                  {candidates.map((comp, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleSelectCompany(comp)}
                      className={styles.modalResultItem}
                    >
                      <div className={styles.modalResultItemTitle}>{comp.name}</div>
                      <div className={styles.modalResultItemAddress}>{comp.address}</div>
                      <div className={styles.modalResultItemNumber}>
                        法人番号: {comp.corporate_number}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.modalEmptyState}>検索結果がここに表示されます。</div>
              )}
            </div>

            <div className={styles.modalNoCorpWrap}>
              <label>
                <input
                  type="checkbox"
                  checked={noCorporateNumber}
                  onChange={(e) => {
                    setNoCorporateNumber(e.target.checked);
                    if (e.target.checked) {
                      setRegTenantName('');
                      setRegCorporateNumber('');
                      setCandidates([]);
                    }
                  }}
                />
                法人番号を持たない（個人事業主・設立準備中など）
              </label>
              {noCorporateNumber && (
                <div className={styles.modalNoCorpInputWrap}>
                  <input
                    type="text"
                    value={regTenantName}
                    onChange={(e) => setRegTenantName(e.target.value)}
                    placeholder="企業名を入力"
                  />
                  <button onClick={() => setShowSearchModal(false)}>確定</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreRegister;
